import React from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  findIconDefinition,
  type IconName,
} from "@fortawesome/fontawesome-svg-core";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useElementValue, useIsReadOnly } from "../DocumentView/DocumentContext";
import { useDefaultValue } from "./useDefaultValue";
import HiddenElementBanner from "./HiddenElementBanner";
import ArchivedElementBanner from "./ArchivedElementBanner";

/**
 * Single band — a percentage range with a colour and optional label.
 * Shape kept loose (no class, just a plain JSON object) so consumers
 * can persist whatever they need. The renderer guarantees `from` /
 * `to` / `colour` are present after any edit.
 */
export interface ColourBand {
  from: number;
  to: number;
  colour: string;
  label?: string;
  /** Outcome icon key (OUTCOME_ICONS) — the certificate variant renders it big. */
  icon?: string;
  /** Outcome description shown on the certificate variant. */
  description?: string;
}

interface Props {
  element: DocumentElementType;
}

const PLUS_ICON = findIconDefinition({ prefix: "far", iconName: "plus" as IconName });
const TRASH_ICON = findIconDefinition({ prefix: "far", iconName: "trash" as IconName });

const isBand = (v: unknown): v is ColourBand =>
  v !== null &&
  typeof v === "object" &&
  typeof (v as ColourBand).from === "number" &&
  typeof (v as ColourBand).to === "number" &&
  typeof (v as ColourBand).colour === "string";

const normaliseBands = (raw: unknown): ColourBand[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isBand);
};

/**
 * Generic colour-band list editor. Each band is one row of (from %,
 * to %, colour, optional label). Designers add as many as they like
 * via the "Add band" button.
 *
 * Currently used in the Score element's configForm for percentage
 * thresholds, but the shape is generic enough to reuse anywhere a
 * "tiered by value with colour" picker is wanted (SLA tiers, billing
 * brackets, KPI bands, etc.).
 */
const ColourBands: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<ColourBand[] | undefined>(
    element.id,
  );
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    helpText?: string;
    hidden?: boolean;
    defaultValue?: ColourBand[];
    /** Tweaks the suffix shown after the from/to inputs (default "%"). */
    unit?: string;
    /** Min allowed for the from input (default 0). */
    min?: number;
    /** Max allowed for the to input (default 100). */
    max?: number;
  };
  const bands = normaliseBands(value);
  const unit = typeof data?.unit === "string" ? data.unit : "%";
  const min = typeof data?.min === "number" ? data.min : 0;
  const max = typeof data?.max === "number" ? data.max : 100;
  const label = data?.label || "Bands";
  const helpText = data?.helpText;

  useDefaultValue(value, setValue, normaliseBands(data?.defaultValue));

  const updateBand = (index: number, patch: Partial<ColourBand>) => {
    const next = bands.slice();
    next[index] = { ...next[index], ...patch };
    setValue(next);
  };

  const removeBand = (index: number) => {
    const next = bands.slice();
    next.splice(index, 1);
    setValue(next);
  };

  const addBand = () => {
    const lastTo = bands.length > 0 ? bands[bands.length - 1].to : min;
    const nextFrom = Math.min(max, lastTo + 1);
    setValue([
      ...bands,
      { from: nextFrom, to: max, colour: "#94a3b8", label: "" },
    ]);
  };

  // Viewing a result: the bands as a static list, never the editor.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{label}</Label>
        {bands.length === 0 ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <ul className="space-y-1">
            {bands.map((band, index) => (
              <li key={index} className="flex items-center gap-2 text-sm">
                <span
                  className="inline-block h-3 w-3 rounded-sm border border-border"
                  style={{ backgroundColor: band.colour }}
                />
                <span>
                  {band.from}
                  {unit} – {band.to}
                  {unit}
                </span>
                {band.label ? (
                  <span className="text-muted-foreground">{band.label}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {helpText ? (
          <p className="text-xs text-muted-foreground">{helpText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{label}</Label>
      {bands.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-3 text-center text-xs text-muted-foreground">
          No bands defined. Click <em>Add band</em> below.
        </p>
      ) : (
        <ul className="space-y-2">
          {bands.map((band, index) => (
            <li
              key={index}
              className="grid grid-cols-[1fr_auto_1fr_auto_2fr_auto] items-center gap-2 rounded-md border border-border bg-background p-2"
            >
              <Input
                type="number"
                min={min}
                max={max}
                value={Number.isFinite(band.from) ? band.from : ""}
                onChange={(e) =>
                  updateBand(index, { from: Number(e.target.value) })
                }
                className="h-9"
                aria-label="From"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="number"
                min={min}
                max={max}
                value={Number.isFinite(band.to) ? band.to : ""}
                onChange={(e) =>
                  updateBand(index, { to: Number(e.target.value) })
                }
                className="h-9"
                aria-label="To"
              />
              <span className="text-xs text-muted-foreground">{unit}</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={band.colour || "#94a3b8"}
                  onChange={(e) =>
                    updateBand(index, { colour: e.target.value })
                  }
                  aria-label="Colour"
                  className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent p-0"
                />
                <Input
                  type="text"
                  value={band.label || ""}
                  onChange={(e) =>
                    updateBand(index, { label: e.target.value })
                  }
                  placeholder="Label (optional)"
                  className="h-9"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeBand(index)}
                aria-label="Remove band"
                className="h-9 w-9 p-0 text-destructive hover:bg-destructive/10"
              >
                {TRASH_ICON ? (
                  <FontAwesomeIcon icon={TRASH_ICON} className="h-3.5 w-3.5" />
                ) : (
                  "×"
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addBand}
        className="gap-1.5"
      >
        {PLUS_ICON ? (
          <FontAwesomeIcon icon={PLUS_ICON} className="h-3 w-3" />
        ) : (
          "+"
        )}
        <span>Add band</span>
      </Button>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default ColourBands;
